import { Calculation, Characteristic, ConversionSystem, Index, Measurement, Property } from '@library';
import { ReportDataContext } from "../advanced-report";
import { ChartContextType } from '../report';

export enum TableDirection {
    VERTICAL = "VERTICAL",
    HORIZONTAL = "HORIZONTAL",
}

export enum TableDataType {
    STATIC = "STATIC",
    DYNAMIC = "DYNAMIC",
}

export enum TableDataPointType {
    CALCULATION = "CALCULATION",
    CHARACTERISTIC = "CHARACTERISTIC",
    INDEX = "INDEX",
    MEASUREMENT = "MEASUREMENT",
    PROPERTY = "PROPERTY",
    CLASSIFICATION = "CLASSIFICATION",
}

export type TableVerticalAlignment =  "top" | "middle" | "bottom";
export type TableHorizontalAlignment =  "left" | "center" | "right";

export interface TableCompiledData {
    columns: TableColumn[];
    meta: TableMetaData[];
    rows: TableRows[];
    cols: string[];
}

export interface BaseColumn {
    id: string;
}

export type TableColumn = MeasurementColumn | CharacteristicColumn | CalculationColumn | IndexColumn | PropertyColumn;

export interface MeasurementColumn extends BaseColumn {
    type: TableDataPointType.MEASUREMENT;
    itemDef: Measurement;
}
export interface CharacteristicColumn extends BaseColumn {
    type: TableDataPointType.CHARACTERISTIC;
    itemDef: Characteristic;
}
export interface CalculationColumn extends BaseColumn {
    type: TableDataPointType.CALCULATION;
    itemDef: Calculation;
}
export interface IndexColumn extends BaseColumn {
    type: TableDataPointType.INDEX;
    itemDef: Index;
}
export interface PropertyColumn extends BaseColumn {
    type: TableDataPointType.PROPERTY;
    itemDef: Property;
}

export interface TableMetaData {
    block: string;
    colour: string;
    eval: string;
    evalStartDate: string;
    evalEndDate: string;
    ref: string;
    sample: string;
    sampleType: string;
    plantSample: string;
    plantSampleDate: string;
    birthDate: string;
    scionCrop: string;
    scionCultivar: string;
    rootstockCultivar: string;
    rootstockCrop: string;
    site: string;
    context: ReportDataContext;
}

export interface TableRows {
    ref: string;
    [key: string]: string | number;
}

export interface TableEntry {
    colSpan: number;
    rowSpan: number;
    content: string | number;
    verticalAlign: TableVerticalAlignment;
    horizontalAlign: TableHorizontalAlignment;
    fill: string;
}

export interface TableGroup {
    entries: TableEntry[]
}

export interface DynamicTableDataPoint {
    id: string;
    type: TableDataPointType;
}

export interface DynamicTableConfig {
    fill: "#fff",
    horizontalAlign: TableHorizontalAlignment,
    verticalAlign: TableVerticalAlignment,
}

export interface BaseTableComponentData {
    label: string | null;
    // Optional so tables saved before this existed resolve to bordered (the prior, only look).
    showBorders?: boolean;
    // Last-known settings for whichever variant (static/dynamic) isn't currently active — carried
    // alongside the active variant's flat fields so switching `type` back and forth in the edit
    // dialog doesn't destroy the other variant's config. Absent on tables saved before this existed.
    inactiveVariant?: StaticTableData | DynamicTableData;
}

export interface StaticTableData extends BaseTableComponentData {
    type: TableDataType.STATIC;
    groups: TableGroup[];
}

export interface DynamicTableData extends BaseTableComponentData {
    type: TableDataType.DYNAMIC;
    context: ChartContextType;
    direction: TableDirection;
    dataPoints: DynamicTableDataPoint[];
    config: DynamicTableConfig;
    system: ConversionSystem;
}

export type TableComponentData = StaticTableData | DynamicTableData;

export const TABLE_ENTRY_DEFAULT: TableEntry = {
    colSpan: 1,
    rowSpan: 1,
    content: null,
    fill: "#fff",
    horizontalAlign: "left",
    verticalAlign: "middle",
}

export const DEFAULT_STATIC_TABLE: StaticTableData = {
    groups: [
        {
            entries: [
                TABLE_ENTRY_DEFAULT,
                TABLE_ENTRY_DEFAULT,
            ],
        },
        {
            entries: [
                TABLE_ENTRY_DEFAULT,
                TABLE_ENTRY_DEFAULT,
            ],
        }
    ],
    type: TableDataType.STATIC,
    label: null,
    showBorders: true,
}

export const DEFAULT_DYNAMIC_TABLE: DynamicTableData = {
    config: {
        fill: "#fff",
        horizontalAlign: "left",
        verticalAlign: "middle",
    },
    context: ChartContextType.CULTIVAR,
    dataPoints: [],
    direction: TableDirection.HORIZONTAL,
    label: null,
    type: TableDataType.DYNAMIC,
    system: ConversionSystem.METRIC,
    showBorders: true,
}

export const DEFAULT_EMPTY_TABLE: TableComponentData = {
    groups: [
        {
            entries: [
                TABLE_ENTRY_DEFAULT,
                TABLE_ENTRY_DEFAULT,
            ],
        },
        {
            entries: [
                TABLE_ENTRY_DEFAULT,
                TABLE_ENTRY_DEFAULT,
            ],
        }
    ],
    type: TableDataType.STATIC,
    label: null,
    showBorders: true,
}

export const TABLE_OPTION_DEFAULT: TableComponentData = {
    groups: [],
    type: TableDataType.STATIC,
    label: null,
    showBorders: true,
}
